<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { noticeService, noticeColorBg, type Notice } from '@/services-new/noticeService';

const showAllEvents = ref(false);
const closed = ref(false);

const events = ref<Notice[]>([]);

const currentIndex = ref(0);
// animationKey changes on each rotation to re-trigger the CSS animation
const animationKey = ref(0);
let intervalId: ReturnType<typeof setInterval> | null = null;

const activeEvent = computed(() => events.value[currentIndex.value]);

const restEvents = computed(() =>
  events.value.filter((_, i) => i !== currentIndex.value)
);

function startRotation() {
  stopRotation();
  intervalId = setInterval(() => {
    currentIndex.value = (currentIndex.value + 1) % events.value.length;
    animationKey.value++;
  }, 10000);
}

function stopRotation() {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

watch(showAllEvents, (val) => {
  if (!val && hasMultipleEvents.value) {
    startRotation();
  } else {
    stopRotation();
  }
});

const hasMultipleEvents = computed(() => events.value.length > 1);

onMounted(async () => {
  events.value = await noticeService.loadInfoNotices();
  if (hasMultipleEvents.value) startRotation();
});

onUnmounted(() => {
  stopRotation();
});

function onLeave(el: Element, done: () => void) {
  const htmlEl = el as HTMLElement;
  htmlEl.style.height = htmlEl.scrollHeight + 'px';
  htmlEl.style.overflow = 'hidden';
  htmlEl.style.transition = 'height 0.35s cubic-bezier(0.22, 1, 0.36, 1)';
  requestAnimationFrame(() => {
    htmlEl.style.height = '0';
    htmlEl.addEventListener('transitionend', done, { once: true });
  });
}
</script>

<template>
  <Transition @leave="onLeave">
    <div
      v-if="events.length && !closed"
      class="relative z-31"
    >
      <!-- Main panel: clips overflow so the slide animation stays contained -->
      <div
        :style="{ backgroundColor: noticeColorBg(activeEvent.color) }"
        class="px-4 pt-8 pb-2 lg:pb-0 lg:pt-0 lg:px-10 lg:px-40 relative min-h-22 lg:min-h-8 overflow-hidden transition-colors duration-400"
      >
        <div v-if="hasMultipleEvents" class="absolute top-0 left-4 lg:left-10 lg:h-full lg:py-0 py-3 flex items-center z-10">
          <button
            class="flex items-center gap-1.5 font-roboto-slab cursor-pointer"
            style="color: #ffffff"
            @click="showAllEvents = !showAllEvents"
          >
            <span class="underline text-xs">
              {{ showAllEvents ? 'Skrýt akce' : 'Všechny akce' }}
            </span>
            <i
              class="text-base"
              :class="showAllEvents ? 'mi-chevron-u' : 'mi-chevron-d'"
            />
          </button>
        </div>

        <!-- Animated content: re-mounts on each rotation via :key -->
        <div
          :key="animationKey"
          class="event-slide pt-2 pb-2"
        >
          <p
            class="text-xs font-roboto-slab text-center line-clamp-2 text-white"
            v-html="activeEvent.text"
          />
        </div>

        <div class="absolute top-0 right-4 lg:right-10 lg:h-full lg:py-0 py-3 flex items-center z-10">
          <button
            class="cursor-pointer"
            @click="closed = true"
          >
            <i
              class="mi-close text-base text-white"
            />
          </button>
        </div>
      </div>

      <!-- Absolute panel: shown when showAllEvents is true, displays the rest -->
      <div
        v-if="showAllEvents"
        class="absolute top-full left-0 w-full z-10"
      >
        <div
          v-for="(event, index) in restEvents"
          :key="index"
          :style="{ backgroundColor: noticeColorBg(event.color) }"
          class="px-4 lg:px-10 lg:px-40 relative min-h-8 py-2"
        >
          <p
            class="text-xs font-roboto-slab text-center text-white"
            v-html="event.text"
          />
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
@keyframes slideFromBottom {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.event-slide {
  animation: slideFromBottom 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
}
</style>
